import React, { ChangeEvent, FocusEvent, ForwardedRef, useCallback } from 'react';

import excludePropsByComponent from '../../../utils/components/_excludePropsByComponent';
import Control, { ControlProps, ControlRenderProps } from '../Control';
import resolveStringValue from '../../../utils/components/_resolveStringValue';
import CheckboxList, { CheckboxListProps } from '../CheckboxList';

export type CheckboxListControlValidationProps = {
  required?: boolean;
};

export interface CheckboxListControlProps<Value, StateValue> extends
  Omit<CheckboxListProps<Value>, 'defaultValue' | 'name' | 'value' | 'isTouched' | 'isValid' | 'label'>,
  Omit<ControlProps<Value[], StateValue[], HTMLInputElement, CheckboxListControlValidationProps>, 'children'>,
  CheckboxListControlValidationProps {
    ref?: ForwardedRef<HTMLInputElement>;
}

interface CheckboxListControlInternalProps<Value> extends Omit<ControlRenderProps<Value[], HTMLInputElement>, 'onValueChange' | 'onTouched' | 'id' | 'onBlur' | 'onChange' | 'onFocus' | 'onKeyDown'> {
  onValueChangeCallback: ControlRenderProps<Value[], HTMLInputElement>['onValueChange'];
  onTouchedCallback: ControlRenderProps<Value[], HTMLInputElement>['onTouched'];
}

export interface CheckboxListControlComposedProps<Value, StateValue> extends CheckboxListControlProps<Value, StateValue>, CheckboxListControlInternalProps<Value> {}

export const CheckboxListControlDefaultProps = {};

const CheckboxListControl = <
  Value extends null | object | string | number | boolean,
  StateValue
>({
    id, value, defaultValue, name, options, readOnly, textOnly, isValid, isTouched, onChange, onBlur, onValueChangeCallback, ...props
  }: CheckboxListControlComposedProps<Value, StateValue>) => {
  const handleValueChange = useCallback((
    {
      event,
      updateOptions,
    }: {
      event: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>;
      updateOptions?: { updateType?: 'blur'};
    },
  ) => {
    const { target } = event;
    const { checked } = target;
    const index = Number(target.getAttribute('data-index'));
    const { value: optionValue, key } = options[index];
    const itemIndex = value.findIndex((selectedOption) => resolveStringValue(selectedOption, key) === resolveStringValue(optionValue, key));
    const itemIncluded = Boolean(itemIndex !== -1);

    if (checked && !itemIncluded) {
      const newValues = [...value, optionValue];
      onValueChangeCallback(newValues, updateOptions);
    } else if (!checked && itemIncluded) {
      const filteredValues: Value[] = value.filter((currentValue, valueIndex) => valueIndex !== itemIndex);
      onValueChangeCallback(filteredValues, updateOptions);
    }
  }, [onValueChangeCallback, options, value]);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const updateOptions = { updateType: 'blur' as const };
    handleValueChange({ event, updateOptions });
    if (onChange) onChange(event);
  }, [onChange, handleValueChange]);

  const handleBlur = useCallback((event: FocusEvent<HTMLInputElement>) => {
    handleValueChange({ event });
    if (onBlur) onBlur(event);
  }, [onBlur, handleValueChange]);

  return (
    <CheckboxList
      id={id}
      data-control
      name={name}
      data-control-nesting="5"
      {...excludePropsByComponent(props, Control)}
      readOnly={readOnly}
      textOnly={textOnly}
      isValid={isValid}
      isTouched={isTouched}
      value={value}
      options={options}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

CheckboxListControl.defaultProps = CheckboxListControlDefaultProps;

export default CheckboxListControl;
